import React from 'react';
import styled from 'styled-components';
import { StyleObject } from '../../types/StyleObject.type';
import { ThemeObject } from '../../assets/themes/default';

// TYPES
/**
 * Sizes for the IconButton
 * @type {Sizes}
 * @example
 * import IconButton, { Sizes as IconButtonSizes } from '../../components/IconButton/IconButton';
 */
export enum Sizes {
  xs = "1rem",
  s = "2rem", 
  m = "3rem",
  l = "4rem",
  xl = "5rem",
}
interface IconButtonProps {
  $color: string,
  $colorhover: string,
  $coloractive: string,
  $size?: Sizes,
  $align?: 'left'|'center'|'right',
}

// STYLES
const Style:StyleObject = {};
Style.IconButton = styled.a<IconButtonProps>`
  color: ${({$color: color}) => color ? color : ({theme}) => theme.colors.c01_70};
  -webkit-tap-highlight-color:  ${({theme}) => theme.colors.c00}; 

  font-size: ${({$size: size}) => size ? size : Sizes.xs};

  text-align: ${({$align: align}) => align ? align : 'center'};
  cursor:pointer;
  transition: color 400ms ease;
  filter: drop-shadow(0 0 1rem currentColor);
  &:hover {
    color: ${({$colorhover: colorhover}) => colorhover ? colorhover : ({theme}) => theme.colors.c01};
  }
  &:active {
    color: ${({$coloractive: coloractive}) => coloractive ? coloractive : ({theme}) => theme.colors.c03};
    transition: color 0s ease;
  }
`;

/**
 * IconButton component
 * @param {string} iconCode - icon code
 * @param {'fa-brands'|'fa-regular'|'fa-solid'} iconType - icon type
 * @param {string} color - color
 * @param {string} colorHover - color on hover
 * @param {string} colorActive - color on active
 * @param {string} href - href
 * @param {'left'|'center'|'right'} textAlign - text align
 * @param {Sizes} size - size
 * @param {(ev: PointerEvent) => void} onClick - on click
 * @returns {React.JSX.Element} React.JSX.Element
 * @example
 * return (
 *  <IconButton 
 *    iconCode='fa-square'
 *    iconType='fa-solid'
 *  />
 * );
 */
const IconButton = ({
  iconCode='fa-square',
  iconType='fa-solid',
  color="",
  colorHover="",
  colorActive="",
  href,
  textAlign='center',
  size=Sizes.xs,
  onClick
}:{
  iconCode?: string,
  iconType?: 'fa-brands'|'fa-regular'|'fa-solid',
  color?: string|(({theme}:{theme:ThemeObject}) => string),
  colorHover?: string|(({theme}:{theme:ThemeObject}) => string),
  colorActive?: string|(({theme}:{theme:ThemeObject}) => string),
  href?: string,
  textAlign?: 'left'|'center'|'right',
  size?: Sizes,
  onClick?: (ev: PointerEvent) => void
}) => {
  return (
    <Style.IconButton 
      $color={color}
      $colorhover={colorHover}
      $coloractive={colorActive}
      href={href} 
      $align={textAlign}
      $size={size} 
      onClick={(ev) => {
        if(onClick) onClick(ev);
      }}
    >
      <i className={iconType + ' ' + iconCode}></i>
    </Style.IconButton>
  )
}

export default IconButton